import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios, { AxiosInstance } from "axios";
import { getApiUrl } from "../config";
import {
  bootstrapTokenFromUrl,
  clearStoredToken,
  getStoredToken,
  storeToken,
} from "../lib/adminTokenStorage";
import { adminApi, AdminApi } from "../lib/adminApi";
import { AdminUser } from "../types/admin";

interface AdminAuthContextValue {
  user: AdminUser | null;
  token: string | null;
  loading: boolean;
  apiClient: AxiosInstance;
  api: AdminApi;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<AdminUser | null>;
  handleUnauthorized: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextValue | undefined>(
  undefined
);

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const apiBase = `${getApiUrl()}/admin`;

  const handleUnauthorized = useCallback(() => {
    setUser(null);
    setToken(null);
    clearStoredToken();
  }, []);

  const apiClient: AxiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: apiBase,
      withCredentials: true,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    instance.interceptors.request.use((config) => {
      if (token) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          handleUnauthorized();
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [apiBase, token, handleUnauthorized]);

  const refresh = useCallback(
    async (tokenOverride?: string): Promise<AdminUser | null> => {
      const effectiveToken = tokenOverride ?? token;
      if (!effectiveToken) {
        setUser(null);
        setLoading(false);
        return null;
      }

      try {
        setLoading(true);
        const response = await apiClient.get("/me", {
          headers: tokenOverride
            ? { Authorization: `Bearer ${tokenOverride}` }
            : undefined,
        });
        const adminUser = (response.data?.user ??
          response.data?.admin) as AdminUser | undefined;
        if (adminUser) {
          setUser(adminUser);
          return adminUser;
        }
        handleUnauthorized();
        return null;
      } catch (error) {
        handleUnauthorized();
        return null;
      } finally {
        setLoading(false);
      }
    },
    [apiClient, token, handleUnauthorized]
  );

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        setLoading(true);
        const response = await axios.post(
          `${apiBase}/login`,
          { email, password },
          { withCredentials: true }
        );

        const issuedToken = response.data?.access_token as string | undefined;
        if (issuedToken) {
          storeToken(issuedToken);
          setToken(issuedToken);
        }

        const userInResponse = (response.data?.user ??
          response.data?.admin) as AdminUser | undefined;
        if (userInResponse) {
          setUser(userInResponse);
        }
        const refreshed = await refresh(issuedToken);
        if (!issuedToken || !refreshed) {
          throw new Error("login-failed");
        }
      } catch (error) {
        handleUnauthorized();
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [apiBase, refresh, handleUnauthorized]
  );

  const logout = useCallback(async () => {
    try {
      await apiClient.post("/logout");
    } catch (error) {
      if (!axios.isAxiosError(error) || error.response?.status !== 401) {
        console.error("Failed to logout", error);
      }
    } finally {
      handleUnauthorized();
    }
  }, [apiClient, handleUnauthorized]);

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      const tokenFromUrl = bootstrapTokenFromUrl();
      const stored = tokenFromUrl ?? getStoredToken();
      if (stored) {
        setToken(stored);
        return;
      }
      setLoading(false);
    };
    bootstrap();
  }, []);

  useEffect(() => {
    if (!token) {
      setUser(null);
      return;
    }
    refresh();
  }, [token, refresh]);

  const api = useMemo(() => adminApi(apiClient), [apiClient]);

  const value: AdminAuthContextValue = {
    user,
    token,
    loading,
    apiClient,
    api,
    login,
    logout,
    refresh,
    handleUnauthorized,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = (): AdminAuthContextValue => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return context;
};
